import React, { RefObject, useEffect, useRef, useState } from "react";
import { Checkbox, Dropdown, Form, Input, Label, Radio, Search, TextArea, Ref } from "semantic-ui-react";
import CurrencyInput from "react-currency-input-field";
import InputMask from "react-input-mask";
import BCToolTip from "./BCToolTip";
import "../../styles/ui.scss";
import { GlobalConstants } from "../constants/GlobalConstants";
import BCPlaces from "./BCPlaces";
import { convertToDollar, convertToMinFraction } from "../../Helpers";

const useBCTextbox = (
  isTextbox: boolean,
  inputId: string,
  inputContainerRef: RefObject<HTMLDivElement | null>,
): void => {
  useEffect(() => {
    const setValueSizeToInputNode = (inputNode: HTMLInputElement): void => {
      inputNode.setAttribute("size", (inputNode.value.length || 1) + "");
    };
    const handleChangeValue = (event: Event): void => {
      setValueSizeToInputNode(event.currentTarget as HTMLInputElement);
    };
    const inputContainerNode = inputContainerRef.current;
    const inputNode: HTMLInputElement =
      inputContainerNode && inputContainerNode.children
        ? inputContainerNode.children[inputId]
        : null;
    if (isTextbox && inputNode && inputContainerNode) {
      setValueSizeToInputNode(inputNode);
      inputContainerNode.classList.add("bc-textbox-container");
      inputNode.addEventListener("input", handleChangeValue);
      return () => {
        inputNode && inputNode.removeEventListener("input", handleChangeValue);
      };
    }
  }, []);
};

const BCFormInput = ({ ...props }) => {
  const searchRef = useRef() as any;
  const inputKey = `${props.field}-${props.inputKey}-id`;
  const [isSearchOpen, setIsSearchOpen] = useState(true as boolean);
  const inputValue = props.value ? props.value : (props.obj && props.obj[props.field]) || (props.obj && props.obj[props.field] === 0) ? props.obj[props.field] : props.value === 0 ? Number(0) : "";
  const inputSelectedValue =  props.obj && props.obj[props.unitField];
  const inputPlaceholder = props.placeholder ? `e.g. ${props.placeholder}` : null;
  const inputContainerRef = useRef<HTMLDivElement>(null);
  useBCTextbox(props.isTextbox, inputKey, inputContainerRef);
  const handleCalculateCharactersRemaining = (value, limit) => {
    if (limit) {
      return limit - value.length;
    }
  };

  useEffect(() => {
    const handleOutsideSearchClick = (e) => {
      if (searchRef.current && searchRef.current.contains(e.target)) {
        setIsSearchOpen(true);
        return;
      }

      setIsSearchOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideSearchClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideSearchClick);
    };
  }, [searchRef]);

  return (
    <React.Fragment>
      {props.type === "plainText" ? <div className="input-plain-text">
        {props.fieldType === "percent" ? `${convertToMinFraction(inputValue, 3, 3)}%` :
          props.fieldType === "dollar" ? convertToDollar(inputValue) : inputValue}
      </div> : <Form.Field className={`form-input-field ${(props.type === "toggle" || props.type === "checkbox") ? "primary-toggle-field" : ""} ${props.fieldClass}`}>
        {(props.actionLabel && props.actionText && props.labelAction) && <label className={props.labelClass ? props.labelClass : "form-input-label"}>
          <span>{props.actionLabel}{props.required && <span className="required-asterix">*</span>}</span>
          <span>{props.tooltip && <BCToolTip tooltip={props.tooltip} />}</span>
          <span
            className={`primary-action-label ${props.actionLabelClass}`}
            onClick={() => props.labelAction()}
            style={props.setColor ? props.setColor : void 0}

          >{props.actionText}</span>
        </label>}
        {props.label && <label className={props.labelClass ? props.labelClass : "form-input-label"}>
          {props.labelIcon}
          {props.label}{props.required && <span className="required-asterix">*</span>}
          {props.tooltip && <BCToolTip tooltip={props.tooltip} />}
          {props.rightLabelElement && <div className="form-input-label-right">{props.rightLabelElement}</div>}
        </label>}
        {props.subLabel && <label className="form-input-subLabel">
          {props.subLabel}
        </label>}
        {props.type === "input" ? <>
          <Ref innerRef={inputContainerRef}>
            <Input
              autoFocus={props.autoFocus}
              id={inputKey}
              className={`primary-input ${props.inputClass}`}
              labelPosition="left"
              placeholder={inputPlaceholder}
              onChange={(e) => props.handleFormChange(e.target.value, props.field)}
              type={props.fieldType === "dollar" ? "number" : props.inputType ? props.inputType : "text"}
              disabled={props.disabled}
              onBlur={props.onBlur}
              onFocus={props.onFocus}
            >
              {(props.fieldType === "dollar" || props.unitType === "$") && <Label basic>$</Label>}
              {props.fieldType === "socialSecurityHidden" ? <InputMask
                id={inputKey}
                value={inputValue}
                mask="xxx-xx-9999"
                maskChar=" "
              /> : props.fieldType === "inputSuffix" ? <Input
              id={inputKey}
              label={<Dropdown value={inputSelectedValue ? inputSelectedValue : props.defaultUnitValue}  onChange={(e, { value }) => props.handleSelectFormChange(value, props.unitField)} options={props.dropdownOptions} />}
              labelPosition="right"
              value={inputValue}
              onChange={(e) => props.handleFormChange(e.target.value, props.field)}
              disabled={props.disabled}
            >
            </Input> : props.fieldType === "socialSecurity" ? <InputMask
                id={inputKey}
                placeholder={inputPlaceholder}
                value={inputValue}
                mask="999-99-9999"
                maskChar=" "
                onChange={(e) => props.handleFormChange(e.target.value, props.field)}
              /> : props.fieldType === "phone" ? <InputMask
                id={inputKey}
                placeholder="e.g. (206) 555-1234"
                mask="(999) 999-9999"
                maskChar=" "
                onChange={(e) => props.handleFormChange(e.target.value, props.field)}
                value={inputValue}
                disabled={props.disabled}
              /> : props.fieldType === "hidden" ? <input
                className="hidden-input"
                value="Hidden"
              /> : props.fieldType === "dollar" || props.fieldType === GlobalConstants.InputTypes.PERCENT || props.fieldType === "float" ||
                props.fieldType === "int" || props.fieldType === GlobalConstants.InputTypes.PERCENTAGE ? <CurrencyInput
                id={inputKey}
                className="primary-currency-input"
                allowDecimals={props.fieldType === "int" ? false : true}
                decimalsLimit={[GlobalConstants.InputTypes.PERCENT, GlobalConstants.InputTypes.PERCENTAGE].indexOf(props.fieldType) > -1 ? 3 : 2}
                decimalScale={props.decimalScale ? props.decimalScale : [GlobalConstants.InputTypes.PERCENT, GlobalConstants.InputTypes.PERCENTAGE].indexOf(props.fieldType) > -1 ? 3 : props.fieldType === "int" ? null : 2}
                placeholder={inputPlaceholder ? inputPlaceholder : undefined}
                value={inputValue}
                onValueChange={(value) => props.handleFormChange(value, props.field)}
                disabled={props.disabled}
                allowNegativeValue={props.allowNegativeValue ? props.allowNegativeValue : false}
              /> : <input id={inputKey} className={props.inputClass || ""} value={inputValue} data-lpignore="true" />}
              {(props.unitType && props.unitType !== "$" && [GlobalConstants.InputTypes.PERCENT, GlobalConstants.InputTypes.PERCENTAGE].indexOf(props.fieldType) > -1) && <Label basic className={props.unitTypeClass}>{props.unitType}</Label>}
            </Input>
          </Ref>
          {props.charLimit && <span className="primary-textarea-limit">{handleCalculateCharactersRemaining(inputValue, props.charLimit)} Characters Remaining</span>}
        </> : props.type === "socialSecurity" ? <Input
          id={inputKey}
          labelPosition="left"
          disabled={props.disabled}
        >
        </Input> : props.type === "radio" ? <React.Fragment>
          {props.inputArray && props.inputArray.map((item, i) => {
            let valueField = item;
            if (item[props.valueField] || item[props.valueField] === false) {
              valueField = item[props.valueField];
            }

            let valueFieldDisplay = undefined as string | undefined;
            if (typeof valueField === "boolean") {
              valueFieldDisplay = valueField.toString();
            }

            let itemLabel = item;
            if (item.name) {
              itemLabel = `${item.name}${item.description ? ` (${item.description})` : ""}`;
            }
            return (
              <Form.Field key={`${props.field}-radio-${i}`}>
                <Radio
                  id={`${inputKey}-radio-${i}`}
                  className={props.radioClass ? props.radioClass : "primary-radio"}
                  label={itemLabel}
                  value={props.value ? props.value : valueFieldDisplay ? valueFieldDisplay : valueField}
                  checked={props.obj[props.field] === valueField}
                  onChange={() => props.handleFormChange(valueField, props.field)}
                  disabled={props.disabled}
                />
              </Form.Field>
            );
          })}
        </React.Fragment> : props.type === "dropdown" ? <React.Fragment><Dropdown
          id={inputKey}
          className={props.inputDropdownClass ? props.inputDropdownClass : "primary-dropdown"}
          value={inputValue}
          placeholder={props.placeholder ? props.placeholder : "Please Select"}
          fluid={props.fluid}
          search
          selection
          options={props.dropdownOptions}
          onChange={(e, { value }) => props.handleFormChange(value, props.field)}
          disabled={props.disabled}
        /></React.Fragment> : props.type === "textarea" ? <React.Fragment>
          <TextArea
            id={`${inputKey}-textarea`}
            onChange={(e) => props.handleFormChange(e.target.value, props.field)}
            placeholder={inputPlaceholder}
            rows={props.rows ? props.rows : 3}
            value={inputValue}
            disabled={props.disabled}
          />
          {props.charLimit && <span className="primary-textarea-limit">{handleCalculateCharactersRemaining(inputValue, props.charLimit)} Characters Remaining</span>}
        </React.Fragment> : props.type === "toggle" ? <React.Fragment>
          <Checkbox
            id={inputKey}
            className="view-toggle"
            toggle
            checked={props.defaultToTrue ? true : props.value}
            onChange={() => props.handleFormChange(!props.value, props.field)}
            defaultChecked={props.defaultToTrue}
            disabled={props.disabled}
          />
        </React.Fragment> : props.type === "calendar" ? <React.Fragment>
          <Input
            id={inputKey}
            className={props.inputCalendarClass ? props.inputCalendarClass : ""}
            onChange={(e) => props.handleFormChange(e.target.value, props.field)}
            placeholder={inputPlaceholder}
            type="date"
            value={inputValue}
            fluid
            disabled={props.disabled}
          />
        </React.Fragment> : props.type === "checkbox" ? <Checkbox
          id={inputKey}
          className={`primary-checkbox ${props.labelClass && props.labelClass}`}
          label={props.checkboxText}
          checked={props.defaultToTrue ? true : props.value}
          defaultChecked={props.defaultToTrue}
          onChange={() => props.handleFormChange(!props.value, props.field)}
          disabled={props.disabled}
        /> : props.type === GlobalConstants.FormInputTypes.PLACES ? <BCPlaces {...props} /> :
          props.type === GlobalConstants.FormInputTypes.SEARCH_FILTER ? <div ref={searchRef}>
            <Search
              icon={props.icon}
              id={props.searchClass ? props.searchClass : `${props.field}-search-id`}
              className={props.searchClass ? props.searchClass : "bc-search-input"}
              value={props.value}
              open={props.isSearchOpen && isSearchOpen}
              results={props.renderSuggestions}
              onSearchChange={(e, { value }) => props.onSearchChange(value, props.field)}
              onResultSelect={props.handleSearchSelect}
              placeholder={props.placeholder ? `e.g. ${props.placeholder}` : ""}
              showNoResults
              onFocus={props.onSearchFocus}
              noResultsMessage={props.noSearchResultsMessage}
              onKeyDown={(e) => props.handleSearchKeyDown(e)}
              disabled={props.disabled}
            />
          </div> : null}
      </Form.Field>}
    </React.Fragment>
  );
};

export default BCFormInput;
